import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Card, CardContent, CardHeader, Divider, Stack, Switch } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { useGetWorkplace } from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { Text, Title, useModalState } from "@src/lib/ui-react";

import { useUpdateWorkplaceInvalidContractWarning } from "./api/useUpdateWorkplaceInvalidContractWarning";
import { EditInvalidContractWarningDialog } from "./EditInvalidContractWarningDialog";

interface FacilityInvalidContractWarningProps {
  workplaceId: string;
}

export function FacilityInvalidContractWarning(props: FacilityInvalidContractWarningProps) {
  const { workplaceId } = props;
  const { data: workplaceProfile } = useGetWorkplace({ workplaceId });

  const settings = workplaceProfile?.data.attributes.invalidContractWarningSettings;
  const currentEnabled = Boolean(settings?.enabled);
  const currentDeadline = settings?.contractFixDeadline;
  const currentDeadlineAsDate = isDefined(currentDeadline)
    ? new Date(currentDeadline + "T12:00:00")
    : undefined;

  const editModalState = useModalState();
  const { mutateAsync: updateWorkplace } = useUpdateWorkplaceInvalidContractWarning();

  const handleDisable = async () => {
    const response = await updateWorkplace({
      workplaceId,
      invalidContractWarningSettings: { enabled: false },
    }).catch(() => undefined);
    if (isDefined(response)) {
      showSuccessToast("Invalid contract warning disabled successfully");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card variant="outlined">
        <CardHeader title="Invalid Contract Warning" />
        <Divider />
        <CardContent sx={{ paddingBottom: 1 }}>
          <EditInvalidContractWarningDialog
            modalState={editModalState}
            workplaceId={workplaceId}
            invalidContractWarningSettings={{
              enabled: currentEnabled,
              ...(isDefined(currentDeadlineAsDate) && {
                contractFixDeadline: currentDeadlineAsDate,
              }),
            }}
            onClose={() => {
              editModalState.closeModal();
            }}
          />
          <Stack direction="column" spacing={2} alignItems="left">
            <Stack direction="row" spacing={1} alignItems="center">
              <Title component="h4">Invalid contract warning</Title>
              <Switch
                checked={currentEnabled}
                onClick={() => {
                  if (currentEnabled) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    handleDisable();
                  } else {
                    editModalState.openModal();
                  }
                }}
              />
            </Stack>
            {isDefined(currentDeadlineAsDate) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Title component="h4">Contract fix deadline:</Title>
                <Text>{formatDate(currentDeadlineAsDate)}</Text>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
}
